import type { WalletsResponse } from "@townsy/contracts";

/**
 * Display helpers for the two operational balances (docs/04 section 1, docs/01 section 6):
 * Coins (whole units, earned from sponsored tasks) and Townsy Credit (integer USD cents,
 * nonwithdrawable). They are ALWAYS shown as two separately labelled values with distinct styles:
 * nothing here adds, compares or converts one into the other.
 */

const integer = new Intl.NumberFormat("en-US");
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

function safeInt(n: number): number {
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/** "1 coin", "500 coins", "12,000 coins". */
export function formatCoins(amount: number): string {
  const n = safeInt(amount);
  return `${integer.format(n)} ${Math.abs(n) === 1 ? "coin" : "coins"}`;
}

/** "$5.00 credit" — Townsy Credit is dollar-denominated but is not cash. */
export function formatCredit(cents: number): string {
  return `${usd.format(safeInt(cents) / 100)} credit`;
}

/** Signed history amount in the unit's own format: "+500 coins", "−20 coins", "+$12.50", "−$3.00". */
export function formatSignedAmount(unit: "COIN" | "CREDIT_USD", amount: number): string {
  const n = safeInt(amount);
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  const abs = Math.abs(n);
  return unit === "COIN" ? `${sign}${formatCoins(abs)}` : `${sign}${usd.format(abs / 100)}`;
}

export interface WalletChip {
  unit: "COIN" | "CREDIT_USD";
  /** Visible text of the chip. */
  text: string;
  /** Full accessible name (screen readers hear the unit, never just a number). */
  ariaLabel: string;
  /** Distinct visual style per unit: amber coin vs cobalt credit (never two gold chips). */
  className: string;
  reservedText: string | null;
}

/** The HUD shows exactly two chips, one per ledger, in a fixed order. */
export function walletChips(w: Pick<WalletsResponse, "coins" | "credit">): [WalletChip, WalletChip] {
  const coins = safeInt(w.coins.available);
  const coinsReserved = safeInt(w.coins.reserved);
  const credit = safeInt(w.credit.availableCents);
  const creditReserved = safeInt(w.credit.reservedCents);
  return [
    {
      unit: "COIN",
      text: formatCoins(coins),
      ariaLabel: `Coins: ${formatCoins(coins)}${coinsReserved > 0 ? `, ${formatCoins(coinsReserved)} reserved` : ""}`,
      className: "wallet-chip wallet-chip-coin",
      reservedText: coinsReserved > 0 ? `${formatCoins(coinsReserved)} reserved` : null,
    },
    {
      unit: "CREDIT_USD",
      text: formatCredit(credit),
      ariaLabel: `Townsy Credit: ${formatCredit(credit)}${creditReserved > 0 ? `, ${formatCredit(creditReserved)} reserved` : ""}`,
      className: "wallet-chip wallet-chip-credit",
      reservedText: creditReserved > 0 ? `${formatCredit(creditReserved)} reserved` : null,
    },
  ];
}
